import { useState } from "react";
import { Plus, Copy, Trash2, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { callSheetApi } from "@/lib/api";

function fmtDateShort(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Tabbed switcher for multi-day call sheets.
 *
 * Props:
 *  - days: Array<CallSheet>  (sorted by day_number ascending)
 *  - activeDayId: string
 *  - onSelect: (dayId) => void
 *  - canEdit: boolean   (admins only)
 *  - projectId: string
 *  - onDaysChanged: (newDays, newActiveId?) => void  // after add/delete
 */
export function DayTabs({
  days,
  activeDayId,
  onSelect,
  canEdit,
  projectId,
  onDaysChanged,
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [renameDay, setRenameDay] = useState(null);
  const [working, setWorking] = useState(false);

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    setWorking(true);
    try {
      await callSheetApi.removeDay(projectId, confirmDeleteId);
      const fresh = await callSheetApi.listForProject(projectId);
      // Pick a new active day — prefer the one before the deleted, else the first
      const idx = days.findIndex((d) => d.id === confirmDeleteId);
      const fallback = fresh[Math.max(0, idx - 1)] || fresh[0];
      onDaysChanged(fresh, fallback?.id);
      setConfirmDeleteId(null);
      toast.success("Shoot cancelled");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || detail || "Failed to remove day");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="border-b border-zinc-800 px-6 lg:px-10 -mx-6 lg:-mx-10">
      <div className="max-w-screen-2xl mx-auto flex items-end gap-1 overflow-x-auto scrollbar-thin">
        {days.map((d) => {
          const isActive = d.id === activeDayId;
          const label = d.day_label?.trim() || `Day ${d.day_number}`;
          return (
            <button
              key={d.id}
              data-testid={`day-tab-${d.day_number}`}
              onClick={() => onSelect(d.id)}
              className={`group relative px-4 py-3 text-sm transition-colors whitespace-nowrap border-b-2 ${
                isActive
                  ? "border-amber-500 text-amber-500"
                  : "border-transparent text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-overline">{label}</span>
                {d.shoot_date && (
                  <span className="text-xs text-zinc-500 font-mono">
                    {fmtDateShort(d.shoot_date)}
                  </span>
                )}
                {canEdit && isActive && (
                  <>
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Rename shoot · ${label}`}
                      title="Rename this shoot day"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRenameDay(d);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          setRenameDay(d);
                        }
                      }}
                      data-testid={`day-tab-rename-${d.day_number}`}
                      className="ml-1 p-0.5 rounded-sm text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 cursor-pointer"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Cancel shoot · ${label}`}
                      title="Cancel this shoot day"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteId(d.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          setConfirmDeleteId(d.id);
                        }
                      }}
                      data-testid={`day-tab-delete-${d.day_number}`}
                      className="ml-0.5 p-0.5 rounded-sm text-zinc-500 hover:text-rose-400 hover:bg-rose-500/10 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </span>
                  </>
                )}
              </div>
            </button>
          );
        })}

        {canEdit && (
          <button
            data-testid="add-day-btn"
            onClick={() => setAddOpen(true)}
            className="ml-2 mb-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-500 hover:bg-amber-500/10 rounded-sm transition-colors border border-dashed border-zinc-700 hover:border-amber-500/60"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Day
          </button>
        )}
      </div>

      <AddDayDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        projectId={projectId}
        days={days}
        onAdded={async (newDay) => {
          const fresh = await callSheetApi.listForProject(projectId);
          onDaysChanged(fresh, newDay?.id);
          setAddOpen(false);
        }}
      />

      <RenameDayDialog
        day={renameDay}
        onOpenChange={(o) => !o && setRenameDay(null)}
        projectId={projectId}
        onSaved={async () => {
          const fresh = await callSheetApi.listForProject(projectId);
          onDaysChanged(fresh, renameDay?.id);
          setRenameDay(null);
        }}
      />

      <Dialog
        open={!!confirmDeleteId}
        onOpenChange={(o) => !o && setConfirmDeleteId(null)}
      >
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-light flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-rose-400" />
              Delete this day?
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              This permanently removes this day&apos;s call sheet, including its
              talent, crew, schedule, and any confirmation records. Public share
              links for this day will stop working.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteId(null)}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-delete-day-btn"
              onClick={handleDelete}
              disabled={working}
              className="bg-rose-600 hover:bg-rose-500 text-white"
            >
              {working ? "Removing…" : "Delete Day"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RenameDayDialog({ day, onOpenChange, projectId, onSaved }) {
  const [label, setLabel] = useState("");
  const [shootDate, setShootDate] = useState("");
  const [saving, setSaving] = useState(false);

  const open = !!day;

  // Seed form values whenever a new day is opened for rename.
  const initialisedForId = useState({ current: null })[0];
  if (day && initialisedForId.current !== day.id) {
    initialisedForId.current = day.id;
    setLabel(day.day_label || "");
    setShootDate(day.shoot_date || "");
  }
  if (!day && initialisedForId.current !== null) {
    initialisedForId.current = null;
  }

  const handleSave = async () => {
    if (!day) return;
    setSaving(true);
    try {
      await callSheetApi.updateDay(projectId, day.id, {
        day_label: label.trim(),
        shoot_date: shootDate || "",
      });
      toast.success("Shoot day updated");
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update shoot day");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="rename-day-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light flex items-center gap-2">
            <Pencil className="w-5 h-5 text-amber-500" />
            Rename shoot day
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Update the label or shoot date for this day. Changes flow through to
            the PDF, share link, and every downstream email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <Label
              htmlFor="rename-day-label"
              className="text-overline text-zinc-400 mb-1.5 block"
            >
              Day label
            </Label>
            <Input
              id="rename-day-label"
              data-testid="rename-day-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={
                day ? `Defaults to "Day ${day.day_number}" if blank` : ""
              }
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              autoFocus
            />
          </div>

          <div>
            <Label
              htmlFor="rename-day-date"
              className="text-overline text-zinc-400 mb-1.5 block"
            >
              Shoot date
            </Label>
            <Input
              id="rename-day-date"
              data-testid="rename-day-date-input"
              type="date"
              value={shootDate}
              onChange={(e) => setShootDate(e.target.value)}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 font-mono"
            />
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
          >
            Cancel
          </Button>
          <Button
            data-testid="rename-day-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-400 text-black"
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddDayDialog({ open, onOpenChange, projectId, days, onAdded }) {
  const [label, setLabel] = useState("");
  const [shootDate, setShootDate] = useState("");
  const [copyFromId, setCopyFromId] = useState(
    days.length > 0 ? days[days.length - 1].id : ""
  );
  const [saving, setSaving] = useState(false);

  // Reset form when reopening
  const handleOpenChange = (v) => {
    if (v) {
      setLabel("");
      setShootDate("");
      setCopyFromId(days.length > 0 ? days[days.length - 1].id : "");
    }
    onOpenChange(v);
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const newDay = await callSheetApi.addDay(projectId, {
        copy_from_day_id: copyFromId || null,
        day_label: label.trim(),
        shoot_date: shootDate,
      });
      toast.success(`Added ${label.trim() || `Day ${newDay.day_number}`}`);
      onAdded(newDay);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add day");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light flex items-center gap-2">
            <Plus className="w-5 h-5 text-amber-500" />
            Add a shoot day
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Adds a new day to this project&apos;s call sheet. Each day has its own
            schedule, PDF, and share link.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <Label
              htmlFor="day-label"
              className="text-overline text-zinc-400 mb-1.5 block"
            >
              Day label (optional)
            </Label>
            <Input
              id="day-label"
              data-testid="add-day-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder='e.g. "Pickup Day" — defaults to Day N'
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>

          <div>
            <Label
              htmlFor="day-date"
              className="text-overline text-zinc-400 mb-1.5 block"
            >
              Shoot date
            </Label>
            <Input
              id="day-date"
              data-testid="add-day-date"
              type="date"
              value={shootDate}
              onChange={(e) => setShootDate(e.target.value)}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>

          {days.length > 0 && (
            <div>
              <Label className="text-overline text-zinc-400 mb-1.5 block flex items-center gap-1.5">
                <Copy className="w-3.5 h-3.5" />
                Copy from
              </Label>
              <Select
                value={copyFromId || "none"}
                onValueChange={(v) => setCopyFromId(v === "none" ? "" : v)}
              >
                <SelectTrigger
                  data-testid="add-day-copy-from"
                  className="bg-zinc-950 border-zinc-800 focus:ring-amber-500"
                >
                  <SelectValue placeholder="Start blank" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="none">Start blank</SelectItem>
                  {days.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.day_label?.trim() || `Day ${d.day_number}`}
                      {d.shoot_date ? ` — ${fmtDateShort(d.shoot_date)}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
                Copies director contact, production notes, locations, talent,
                crew, and clients. Call times, weather, and date stay blank so
                you can set them fresh.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
          >
            Cancel
          </Button>
          <Button
            data-testid="create-day-btn"
            onClick={handleCreate}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-400 text-black"
          >
            {saving ? "Adding…" : "Add Day"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
